import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Plus, Download, X, ShieldCheck } from "lucide-react";
import { api, errMsg, fmtDateTime, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Field, SelectField } from "@/components/crm/fields";
import { PageHeader } from "@/components/crm/bits";

const Section = ({ title, text, children, testid }) => (
  <section className="card-surface p-5 sm:p-6" data-testid={testid}>
    <h2 className="font-display text-base font-semibold">{title}</h2>
    {text && <p className="text-sm text-gray-500 mt-1 mb-5">{text}</p>}
    {children}
  </section>
);
const ROLE_OPTS = [["admin", "Admin"], ["manager", "Manager"], ["sales", "Sales employee"]];
const useSave = () => {
  const { setSettings } = useMeta();
  return async (body) => { try { const r = await api.put("/settings", body); setSettings(r.data); toast.success("Settings saved"); } catch (e) { toast.error(errMsg(e)); } };
};

function Profile() {
  const { user } = useAuth();
  return (
    <Section title="My profile" testid="settings-profile">
      <div className="grid sm:grid-cols-3 gap-4 text-sm mt-4">
        {[["Name", user.name], ["Email", user.email], ["Role", ROLE_OPTS.find((r) => r[0] === user.role)?.[1]]].map(([k, v]) => <div key={k}><p className="text-xs text-gray-400">{k}</p><p className="mt-0.5 font-medium">{v}</p></div>)}
      </div>
      <p className="text-xs text-gray-500 mt-5 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#0B3C2D]" />{user.role === "sales" ? "You can see and update leads assigned to you." : "You can see all leads and allocate them to the team."}</p>
    </Section>
  );
}

function GmailCard() {
  const [s, setS] = useState(null);
  const load = () => api.get("/gmail/status").then((r) => setS(r.data));
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search).get("gmail");
    if (p === "connected") toast.success("Gmail connected");
    if (p === "failed") toast.error("Gmail connection failed — please try again");
  }, []);
  const connect = async () => { try { const r = await api.post("/gmail/connect"); window.location.href = r.data.url; } catch (e) { toast.error(errMsg(e)); } };
  const disconnect = async () => { await api.post("/gmail/disconnect"); toast.success("Gmail disconnected"); load(); };
  if (!s || !s.configured) return null;
  return (
    <Section title="Gmail" text="Connect your Gmail so approved emails (with attachments) are sent straight to customers from your own address." testid="settings-gmail">
      {s.connected ? (
          <div className="flex flex-wrap items-center gap-3"><span className="text-sm">Connected as <strong data-testid="gmail-connected-email">{s.email}</strong></span><Button variant="outline" size="sm" onClick={disconnect} data-testid="gmail-disconnect-button">Disconnect</Button></div>
        ) : <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={connect} data-testid="gmail-connect-button">Connect Gmail</Button>}
    </Section>
  );
}

function JourneyEmails() {
  const { settings, stages } = useMeta();
  const save = useSave();
  const [tpl, setTpl] = useState(settings.email_templates || {});
  const [review, setReview] = useState(settings.gmb_review_url || "");
  const [open, setOpen] = useState(null);
  const set = (k, f, v) => setTpl({ ...tpl, [k]: { enabled: true, subject: "", body: "", ...tpl[k], [f]: v } });
  return (
    <Section title="Client journey emails" text="When a lead moves into a stage, a draft is created from that stage's template for the owner to review and send. Placeholders: {contact_name} {company} {product} {sender_name} {stage} {review_link}" testid="settings-journey">
      <Field label="Google Business review link (used in the Won email)" className="max-w-xl mb-5">
        <Input value={review} onChange={(e) => setReview(e.target.value)} placeholder="https://g.page/r/..." className="h-9" data-testid="gmb-review-input" />
      </Field>
      <div className="divide-y divide-gray-100 border-y border-gray-100">
        {stages.map((s) => {
          const t = tpl[s.key] || { enabled: false, subject: "", body: "" };
          return (
            <div key={s.key} className="py-3" data-testid={`journey-row-${s.key}`}>
              <div className="flex items-center gap-3">
                <Switch checked={!!t.enabled} onCheckedChange={(v) => set(s.key, "enabled", v)} data-testid={`journey-enabled-${s.key}`} />
                <button className="flex-1 text-left min-w-0" onClick={() => setOpen(open === s.key ? null : s.key)} data-testid={`journey-toggle-${s.key}`}>
                  <span className="text-sm font-medium">{s.name}</span><span className="block text-xs text-gray-500 truncate">{t.subject || "No template"}</span>
                </button>
              </div>
              {open === s.key && (
                <div className="space-y-2 mt-3 fade-in">
                  <Input value={t.subject} onChange={(e) => set(s.key, "subject", e.target.value)} placeholder="Subject" className="h-9" data-testid={`journey-subject-${s.key}`} />
                  <textarea rows={8} value={t.body} onChange={(e) => set(s.key, "body", e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" data-testid={`journey-body-${s.key}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-end mt-4"><Button className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={() => save({ email_templates: Object.fromEntries(Object.entries(tpl).filter(([, v]) => v.subject && v.body)), gmb_review_url: review })} data-testid="save-journey-button">Save journey emails</Button></div>
    </Section>
  );
}

function UsersSection() {
  const { users, refresh } = useMeta();
  const { user: me } = useAuth();
  const [inv, setInv] = useState({ name: "", email: "", role: "sales" });
  const upd = async (u, body) => { try { await api.patch(`/users/${u.user_id}`, body); toast.success("User updated"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const invite = async () => { try { await api.post("/users", inv); toast.success(`${inv.name} can now sign in with Google`); setInv({ name: "", email: "", role: "sales" }); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Section title="Users & permissions" text="Add team members by their Google email. New sign-ups wait here until you activate them." testid="settings-users">
      <div className="grid sm:grid-cols-[1fr_1fr_160px_auto] gap-2 mb-5">
        <Input placeholder="Full name" value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className="h-9" data-testid="invite-name-input" />
        <Input placeholder="name@company.com" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} className="h-9" data-testid="invite-email-input" />
        <SelectField testid="invite-role-select" value={inv.role} onChange={(v) => setInv({ ...inv, role: v })} options={ROLE_OPTS} allowNone={false} />
        <Button className="bg-[#0B3C2D] hover:bg-[#072B20] h-9" disabled={!inv.name || !inv.email} onClick={invite} data-testid="invite-user-button"><Plus className="h-4 w-4 mr-1" />Add</Button>
      </div>
      <div className="divide-y divide-gray-50">
        {users.map((u) => (
          <div key={u.user_id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`user-row-${u.user_id}`}>
            <div className="min-w-0 flex-1"><p className="text-sm font-medium">{u.name}{u.is_sample && <span className="ml-2 text-[10px] uppercase text-gray-400 ring-1 ring-gray-200 rounded px-1">Sample</span>}{!u.active && <span className="ml-2 text-[11px] text-amber-700 bg-amber-50 rounded px-1.5">Awaiting approval</span>}</p><p className="text-xs text-gray-500">{u.email}</p></div>
            <div className="w-40"><SelectField testid={`user-role-${u.user_id}`} value={u.role} onChange={(v) => upd(u, { role: v })} options={ROLE_OPTS} allowNone={false} /></div>
            <label className="flex items-center gap-2 text-xs text-gray-500" title="CC this person on every customer email">CC<Switch checked={!!u.cc_emails} onCheckedChange={(v) => upd(u, { cc_emails: v })} data-testid={`user-cc-${u.user_id}`} /></label>
            <label className="flex items-center gap-2 text-xs text-gray-500">Active<Switch checked={!!u.active} disabled={u.user_id === me.user_id} onCheckedChange={(v) => upd(u, { active: v })} data-testid={`user-active-${u.user_id}`} /></label>
          </div>
        ))}
      </div>
    </Section>
  );
}

function PipelineSection() {
  const { settings } = useMeta();
  const save = useSave();
  const [st, setSt] = useState(settings.stages);
  useEffect(() => setSt(settings.stages), [settings.stages]);
  const set = (i, k, v) => setSt(st.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const move = (i, d) => { const n = [...st]; [n[i], n[i + d]] = [n[i + d], n[i]]; setSt(n); };
  return (
    <Section title="Pipeline stages" text="Rename, reorder or add stages. 'Group' controls the dashboard pipeline summary. Stages with leads cannot be removed." testid="settings-pipeline">
      <div className="space-y-2">
        {st.map((s, i) => (
          <div key={s.key || i} className="grid grid-cols-[1fr_1fr] sm:grid-cols-[1.4fr_1fr_130px_auto_auto] gap-2 items-center" data-testid={`stage-row-${i}`}>
            <Input value={s.name} onChange={(e) => set(i, "name", e.target.value)} className="h-9" data-testid={`stage-name-${i}`} />
            <Input value={s.group} onChange={(e) => set(i, "group", e.target.value)} className="h-9" placeholder="Group" />
            <SelectField value={s.outcome || ""} onChange={(v) => set(i, "outcome", v || null)} options={[["won", "Won"], ["lost", "Lost"]]} placeholder="Open stage" />
            <label className="flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap"><Switch checked={!!s.awaiting} onCheckedChange={(v) => set(i, "awaiting", v)} />Awaiting customer</label>
            <div className="flex">
              <Button size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" disabled={i === st.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setSt(st.filter((_, j) => j !== i))} data-testid={`stage-remove-${i}`}><Trash2 className="h-4 w-4 text-gray-400" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={() => setSt([...st, { name: "New stage", group: "Other", outcome: null, awaiting: false }])} data-testid="add-stage-button"><Plus className="h-4 w-4 mr-1" />Add stage</Button>
        <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={() => save({ stages: st })} data-testid="save-pipeline-button">Save pipeline</Button>
      </div>
    </Section>
  );
}

function ListEditor({ field, title }) {
  const { settings } = useMeta();
  const save = useSave();
  const [items, setItems] = useState(settings[field]);
  const [v, setV] = useState("");
  return (
    <div data-testid={`list-editor-${field}`}>
      <p className="text-sm font-medium mb-2">{title}</p>
      <div className="flex flex-wrap gap-1.5 mb-3">{items.map((x) => <span key={x} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs">{x}<button onClick={() => setItems(items.filter((y) => y !== x))}><X className="h-3 w-3 text-gray-400" /></button></span>)}</div>
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder={`Add ${title.toLowerCase().slice(0, -1)}`} className="h-9" data-testid={`list-input-${field}`} onKeyDown={(e) => { if (e.key === "Enter" && v.trim()) { setItems([...items, v.trim()]); setV(""); } }} />
        <Button variant="outline" onClick={() => { if (v.trim()) { setItems([...items, v.trim()]); setV(""); } }}>Add</Button>
        <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={() => save({ [field]: items })} data-testid={`list-save-${field}`}>Save</Button>
      </div>
    </div>
  );
}

function Escalation({ editable }) {
  const { settings, stages } = useMeta();
  const save = useSave();
  const [e, setE] = useState(settings.escalation);
  const [running, setRunning] = useState(false);
  const run = async () => { setRunning(true); try { const r = await api.post("/admin/run-escalation"); toast.success(`${r.data.overdue} overdue, ${r.data.approaching} approaching, ${r.data.eyt_missing ?? 0} missing EYT — notifications sent`); } catch (x) { toast.error(errMsg(x)); } finally { setRunning(false); } };
  return (
    <Section title="Notifications & escalation rules" text="The stage timer only resets on real actions (calls, meetings, notes, stage moves, completed follow-ups) — never on emails or reminders. Checks run hourly; digest emails go out daily at 9:00 AM IST." testid="settings-escalation">
      <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
        <Field label="Overdue after (days without action)"><Input type="number" min="1" disabled={!editable} value={e.limit_days} onChange={(x) => setE({ ...e, limit_days: parseInt(x.target.value || 0, 10) })} className="h-9" data-testid="esc-limit-input" /></Field>
        <Field label="Send reminder after (days)"><Input type="number" min="0" disabled={!editable} value={e.warn_days} onChange={(x) => setE({ ...e, warn_days: parseInt(x.target.value || 0, 10) })} className="h-9" data-testid="esc-warn-input" /></Field>
      </div>
      <div className="space-y-3 mt-5">
        {[["notify_owner", "Remind the assigned employee"], ["escalate_to_managers", "Escalate overdue leads to managers (in-app + email)"], ["daily_digest", "Daily email digest of due & overdue follow-ups"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-3 text-sm"><Switch disabled={!editable} checked={!!e[k]} onCheckedChange={(v) => setE({ ...e, [k]: v })} data-testid={`esc-${k}`} />{l}</label>
        ))}
        <label className="flex items-center gap-3 text-sm"><Switch disabled={!editable} checked={e.eyt_reminder !== false} onCheckedChange={(v) => setE({ ...e, eyt_reminder: v })} data-testid="esc-eyt_reminder" />Remind owners to capture EYT once a lead moves beyond</label>
        <div className="max-w-sm pl-12"><SelectField testid="esc-eyt-stage-select" value={e.eyt_after_stage || stages[0]?.key} onChange={(v) => setE({ ...e, eyt_after_stage: v })} options={stages.filter((s) => !s.outcome).map((s) => [s.key, s.name])} allowNone={false} /></div>
      </div>
      <div className="flex flex-wrap gap-2 mt-6">
        {editable && <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" onClick={() => save({ escalation: e })} data-testid="save-escalation-button">Save rules</Button>}
        <Button variant="outline" disabled={running} onClick={run} data-testid="run-escalation-button">{running ? "Checking…" : "Run check now"}</Button>
      </div>
    </Section>
  );
}

function Integrations() {
  const { settings } = useMeta();
  const save = useSave();
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/admin/system-status").then((r) => setStatus(r.data)); }, []);
  const Pill = ({ ok }) => <span className={`text-[11px] rounded px-1.5 py-0.5 ${ok ? "bg-[#ECF5F1] text-[#0B3C2D]" : "bg-gray-100 text-gray-500"}`}>{ok ? "Connected" : "Not configured"}</span>;
  return (<>
    <Section title="Email integration" testid="settings-email">
      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between gap-3"><span>Internal notifications & digests (Emergent managed email)</span><Pill ok={status?.email_configured} /></div>
        <div className="flex items-center justify-between gap-3"><span>Customer emails — approved by the employee, then sent from their own mailbox (Gmail / Outlook) with CC recipients pre-filled</span><Pill ok /></div>
        <label className="flex items-center gap-3 pt-2"><Switch checked={false} disabled data-testid="auto-send-switch" />Automatic sending to customers <span className="text-xs text-gray-400">(off — every email requires approval)</span></label>
      </div>
    </Section>
    <Section title="AI settings" testid="settings-ai">
      <div className="flex items-center justify-between text-sm"><span>AI qualification & email drafting (Claude Sonnet 5.5)</span><Pill ok={status?.ai_configured} /></div>
      <label className="flex items-center gap-3 text-sm mt-4"><Switch checked={settings.ai?.enabled !== false} onCheckedChange={(v) => save({ ai: { enabled: v } })} data-testid="ai-enabled-switch" />Enable AI features for all users</label>
    </Section>
  </>);
}

function DataSecurity() {
  const [logs, setLogs] = useState([]);
  const [archived, setArchived] = useState([]);
  const [status, setStatus] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const { refresh, bump } = useMeta();
  const load = () => { api.get("/audit-logs", { params: { limit: 60 } }).then((r) => setLogs(r.data)); api.get("/archived-leads").then((r) => setArchived(r.data)); api.get("/admin/system-status").then((r) => setStatus(r.data)); };
  useEffect(load, []);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); refresh(); bump(); } catch (e) { toast.error(errMsg(e)); } };
  return (<>
    <Section title="Backup & sample data" text="Download a full JSON backup at any time. Sample records are labelled and kept separate from real customer data." testid="settings-data">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => downloadFile("/admin/backup", "thakker-one-backup.json")} data-testid="backup-download-button"><Download className="h-4 w-4 mr-1.5" />Download backup</Button>
        {status?.sample_leads > 0 ? <Button variant="outline" className="text-red-600" onClick={() => setConfirm(true)} data-testid="clear-sample-button">Remove sample data ({status.sample_leads})</Button>
          : <Button variant="outline" onClick={() => act(() => api.post("/admin/sample-data"), "Sample data loaded")} data-testid="load-sample-button">Load sample data</Button>}
      </div>
      <p className="text-xs text-gray-500 mt-3">{status ? `${status.real_leads} real lead(s) · ${status.sample_leads} sample lead(s)` : ""}</p>
    </Section>
    <Section title="Archived leads" text="Leads are never hard-deleted. Restore them here." testid="settings-archived">
      {archived.length === 0 ? <p className="text-sm text-gray-500">No archived leads.</p> : archived.map((a) => (
        <div key={a.lead_id} className="flex items-center justify-between py-2 text-sm border-b border-gray-50 last:border-0"><span>{a.company}</span><Button size="sm" variant="outline" onClick={() => act(() => api.post(`/leads/${a.lead_id}/restore`), "Lead restored")} data-testid={`restore-${a.lead_id}`}>Restore</Button></div>
      ))}
    </Section>
    <Section title="Audit log" text="Recent sign-ins, edits, assignments, exports and settings changes." testid="settings-audit">
      <div className="max-h-96 overflow-y-auto divide-y divide-gray-50 text-sm">
        {logs.map((l) => <div key={l.log_id} className="py-2 flex justify-between gap-3"><span><span className="font-medium">{l.user_name}</span> <span className="text-gray-600">{l.action.replace(/_/g, " ")}{l.action.includes(l.entity) ? "" : ` ${l.entity}`}</span></span><span className="text-xs text-gray-400 whitespace-nowrap">{fmtDateTime(l.created_at)}</span></div>)}
      </div>
    </Section>
    <AlertDialog open={confirm} onOpenChange={setConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Remove all sample data?</AlertDialogTitle><AlertDialogDescription>Only records marked as sample (and the sample employees) are removed. Your real leads are not affected.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="confirm-clear-sample" onClick={() => act(() => api.delete("/admin/sample-data"), "Sample data removed")}>Remove</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>);
}

export default function Settings() {
  const { can } = useAuth();
  const { settings } = useMeta();
  const admin = can("settings:manage");
  const tabs = [["profile", "Profile", true], ["users", "Users & permissions", can("users:manage")], ["pipeline", "Pipeline stages", admin],
    ["lists", "Lead sources & industries", admin], ["journey", "Journey emails", admin], ["escalation", "Notifications & escalation", can("escalation:run")], ["integrations", "Email & AI", admin], ["data", "Data & security", can("audit:view")]].filter((t) => t[2]);
  const [tab, setTab] = useState("profile");
  if (!settings) return null;
  return (
    <div className="fade-in" data-testid="settings-page">
      <PageHeader title="Settings" subtitle={admin ? "Manage your team, pipeline and system configuration." : "Your account"} />
      <div className="grid lg:grid-cols-[220px_1fr] gap-5 items-start">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto" data-testid="settings-nav">
          {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`} className={`text-left whitespace-nowrap px-3 py-2 rounded-lg text-sm ${tab === k ? "bg-[#ECF5F1] text-[#0B3C2D] font-medium" : "text-gray-600 hover:bg-gray-50"}`}>{l}</button>)}
        </nav>
        <div className="space-y-4 min-w-0">
          {tab === "profile" && <><Profile /><GmailCard /></>}
          {tab === "journey" && <JourneyEmails />}
          {tab === "users" && <UsersSection />}
          {tab === "pipeline" && <PipelineSection />}
          {tab === "lists" && <Section title="Lead sources & industries" testid="settings-lists"><div className="space-y-6 mt-4"><ListEditor field="sources" title="Lead sources" /><ListEditor field="industries" title="Industries" /></div></Section>}
          {tab === "escalation" && <Escalation editable={admin} />}
          {tab === "integrations" && <Integrations />}
          {tab === "data" && <DataSecurity />}
        </div>
      </div>
    </div>
  );
}
